import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, KeyRound } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import MagneticButton from "@/components/site/MagneticButton";
import { useSiteContent } from "@/lib/SiteContentProvider";
import { useTheme } from "@/components/site/ThemeProvider";

const NAV_LINKS = [
    { id: "home", to: "/", label: "Home" },
    { id: "about", to: "/about", label: "About" },
    { id: "services", to: "/services", label: "Services" },
    { id: "work", to: "/work", label: "Work" },
    { id: "global", to: "/global", label: "Global" },
    { id: "contact", to: "/contact", label: "Contact" },
];

/**
 * BrandMark — header logo. Prefers a CMS-uploaded logo based on the current
 * theme; when `light` is set (transparent nav over a dark hero scrim) it uses
 * the light/white logo + a white fallback glyph so it always reads.
 */
export const BrandMark = ({ compact = false, light = false, onLight = false }) => {
    const { data } = useSiteContent();
    const { theme } = useTheme();
    const logos = data?.logos || {};
    const themedUrl = (theme === "light" ? logos.light_theme_url : logos.dark_theme_url) || "";
    const url = onLight
        ? (logos.light_theme_url || themedUrl)
        : light ? (logos.dark_theme_url || themedUrl) : themedUrl;
    const alt = logos.alt_text || "Pranto.Gfx";
    const glyph = onLight ? "#121417" : (light ? "#FFFFFF" : "var(--pg-fg)");

    if (url) {
        return (
            <span className={`pg-brand-mark inline-flex items-center ${compact ? "pg-brand-mark--compact" : ""}`} aria-label={alt}>
                <img src={url} alt={alt} loading="eager" decoding="async" className="pg-brand-mark__img" draggable={false} />
            </span>
        );
    }

    return (
        <>
            <svg width={compact ? 22 : 28} height={compact ? 22 : 28} viewBox="0 0 28 28" fill="none" aria-hidden>
                <rect x="1.5" y="1.5" width="25" height="25" rx="7" stroke={glyph} strokeOpacity="0.4" />
                <path d="M9 19V9h5.4c2 0 3.3 1.2 3.3 3.1S16.4 15.2 14.4 15.2H11" stroke={glyph} strokeWidth="1.6" strokeLinecap="round" />
                <circle cx="21" cy="21" r="1.5" fill="var(--pg-accent)" />
            </svg>
            <span className={`font-display ${compact ? "text-sm" : "text-base sm:text-lg"}`} style={{ color: glyph }}>
                Pranto<span style={{ color: "var(--pg-accent)" }}>.</span>Gfx
            </span>
        </>
    );
};

export const Nav = ({ onStartProject }) => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const isActive = (to) => (to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`));

    // Tone: interior-page heroes are dark (white nav); the HOME hero is now a
    // light off-white stage (dark nav). Scrolled = theme glass on dark sections.
    const isHome = pathname === "/";
    const overLightHero = isHome && !scrolled;
    const onDark = !scrolled && !overLightHero;
    // Over light hero → strong dark text; scrolled → off-white on dark glass.
    const fg = overLightHero ? "#0E1013" : (onDark ? "#FFFFFF" : "#F7F5F0");
    const fgMuted = overLightHero ? "rgba(14,16,19,0.82)" : (onDark ? "rgba(255,255,255,0.74)" : "rgba(247,245,240,0.74)");

    const goClientAccess = () => {
        setOpen(false);
        window.location.assign("/delivery");
    };

    return (
        <motion.header
            initial={{ y: -24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-x-0 top-0 z-50"
        >
            {/* Top scrim keeps a transparent nav readable over any hero */}
            {onDark && (
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 top-0 h-28"
                    style={{ background: "linear-gradient(180deg, rgba(4,12,11,0.55) 0%, rgba(4,12,11,0) 100%)" }}
                />
            )}

            <div
                className="mx-auto flex items-center justify-between px-5 sm:px-6 lg:px-8"
                style={{
                    height: scrolled ? "60px" : "78px",
                    transitionProperty: "height, background-color, border-color, box-shadow",
                    transitionDuration: "320ms",
                    transitionTimingFunction: "cubic-bezier(0.23, 1, 0.32, 1)",
                    background: scrolled ? "color-mix(in srgb, var(--pg-bg) 72%, transparent)" : "transparent",
                    backdropFilter: scrolled ? "blur(16px) saturate(140%)" : "none",
                    WebkitBackdropFilter: scrolled ? "blur(16px) saturate(140%)" : "none",
                    borderBottom: scrolled ? "1px solid var(--pg-border)" : "1px solid transparent",
                    boxShadow: scrolled ? "0 10px 34px rgba(0,0,0,0.10)" : "none",
                }}
            >
                <Link to="/" data-testid="nav-logo" className="group flex items-center gap-2" aria-label="Pranto.Gfx home">
                    <BrandMark light={onDark} onLight={overLightHero} />
                </Link>

                <nav className="hidden items-center gap-5 lg:flex xl:gap-7" aria-label="Primary">
                    {NAV_LINKS.map((link) => {
                        const active = isActive(link.to);
                        return (
                            <Link
                                key={link.id}
                                to={link.to}
                                data-testid={`nav-link-${link.id}`}
                                aria-current={active ? "page" : undefined}
                                className="pg-link-underline relative text-sm"
                                style={{ color: active ? fg : fgMuted }}
                            >
                                {link.label}
                                {active && (
                                    <span aria-hidden className="absolute -bottom-1.5 left-0 h-px w-full" style={{ background: "var(--pg-accent)" }} />
                                )}
                            </Link>
                        );
                    })}
                    <a
                        href="/delivery"
                        data-testid="nav-link-client-access"
                        className="pg-link-underline inline-flex items-center gap-1.5 text-sm"
                        style={{ color: fgMuted }}
                    >
                        <KeyRound className="h-3.5 w-3.5" strokeWidth={1.6} style={{ color: "var(--pg-accent)" }} />
                        Client Access
                    </a>
                </nav>

                <div className="flex items-center gap-2 sm:gap-3">
                    <MagneticButton
                        type="button"
                        onClick={() => onStartProject?.()}
                        data-testid="nav-start-project-button"
                        className={`hidden sm:inline-flex ${overLightHero ? "pg-btn-primary" : "pg-btn-ghost pg-btn-glass-lume-gold"}`}
                    >
                        <span>Start A Project</span>
                    </MagneticButton>

                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                data-testid="nav-mobile-menu-button"
                                aria-label="Open menu"
                                className="inline-flex h-10 w-10 items-center justify-center rounded-full border lg:hidden"
                                style={{
                                    borderColor: onDark ? "rgba(255,255,255,0.3)" : (overLightHero ? "rgba(18,20,23,0.2)" : "var(--pg-border)"),
                                    background: onDark ? "rgba(255,255,255,0.08)" : (overLightHero ? "rgba(18,20,23,0.04)" : "var(--pg-surface)"),
                                    color: fg,
                                }}
                            >
                                <Menu className="h-4 w-4" strokeWidth={1.6} />
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="right"
                            className="w-[88%] border-l sm:w-[420px]"
                            style={{ background: "var(--pg-bg)", borderColor: "var(--pg-border)" }}
                        >
                            <div className="flex items-center justify-between">
                                <span className="inline-flex items-center gap-2">
                                    <BrandMark compact />
                                </span>
                                <button onClick={() => setOpen(false)} aria-label="Close menu" className="rounded-full p-2">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <nav className="mt-10 flex flex-col gap-1" aria-label="Mobile">
                                <AnimatePresence>
                                    {NAV_LINKS.map((link, idx) => {
                                        const active = isActive(link.to);
                                        return (
                                            <motion.button
                                                key={link.id}
                                                initial={{ opacity: 0, x: 18 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.04 * idx, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                                onClick={() => { setOpen(false); navigate(link.to); }}
                                                data-testid={`nav-mobile-link-${link.id}`}
                                                aria-current={active ? "page" : undefined}
                                                className="flex items-center justify-between border-b py-5 text-left font-display text-3xl"
                                                style={{ borderColor: "var(--pg-border)", color: active ? "var(--pg-accent)" : "var(--pg-fg)" }}
                                            >
                                                <span>{link.label}</span>
                                                <span style={{ color: "var(--pg-accent)" }}>{"\u2192"}</span>
                                            </motion.button>
                                        );
                                    })}
                                    <motion.button
                                        key="client-access"
                                        initial={{ opacity: 0, x: 18 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.04 * NAV_LINKS.length, duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                        onClick={goClientAccess}
                                        data-testid="nav-mobile-link-client-access"
                                        className="flex items-center justify-between border-b py-5 text-left font-display text-3xl"
                                        style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg)" }}
                                    >
                                        <span className="inline-flex items-center gap-3">
                                            <KeyRound className="h-5 w-5" style={{ color: "var(--pg-accent)" }} strokeWidth={1.6} />
                                            Client Access
                                        </span>
                                        <span style={{ color: "var(--pg-accent)" }}>{"\u2192"}</span>
                                    </motion.button>
                                </AnimatePresence>
                            </nav>
                            <button
                                type="button"
                                onClick={() => { setOpen(false); onStartProject?.(); }}
                                data-testid="nav-mobile-start-project-button"
                                className="pg-btn-primary mt-10 w-full"
                            >
                                Start A Project
                            </button>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </motion.header>
    );
};

export default Nav;
